import { useEffect, useState } from "react";
import { listRequests, updateRequest, type ChangeRequest } from "./actions";
import { downloadCsv } from "./csv";

// Requests: changes someone asked for (today: brand exclusions and restorations), tracked through three steps because the console is not connected to
// WooCommerce or Merchant Center yet: (1) applied in the console (checked from the data, not ticked), (2) done on WooCommerce, (3) done on Merchant Center
// (2 and 3 are ticked by a person, with their name and the time, until those connections exist). A request closes when all three are done.
// Vendors usually ask for immediate action or 24-48 hours, so the due time and "overdue" are shown first.

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const when = (s?: string) => (s ? s.slice(0, 16).replace("T", " ") : "");

export function RequestsPanel() {
  const [rows, setRows] = useState<ChangeRequest[]>([]);
  const [status, setStatus] = useState<"open" | "closed" | "all">("open");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await listRequests(status);
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null); setRows(r.data.rows);
  };
  useEffect(() => { load(); }, [status]);

  const tick = async (id: string, step: "woocommerce" | "merchant", done: boolean) => {
    const r = await updateRequest(id, step, done);
    if (!r.ok) { setError(r.error); return; }
    await load();
  };

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>Requests</h1>
          <span className="meta">
            Each request goes through three steps: applied in the console (checked automatically), done on WooCommerce, done on Merchant Center. The last two are ticked
            by a person until the connections exist. Overdue means past the due time and not finished.
          </span>
        </div>
        <div className="tabs">
          {(["open", "closed", "all"] as const).map((s) => <button key={s} className={status === s ? "active" : ""} onClick={() => setStatus(s)}>{s[0].toUpperCase() + s.slice(1)}</button>)}
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="sheet-actions">
          <button type="button" className="sheet-export" disabled={!rows.length}
                  onClick={() => downloadCsv("hec-requests.csv", [["requested", "request", "urgency", "due", "requested by", "applied in console", "WooCommerce done", "Merchant Center done", "note"],
                    ...rows.map((r) => [r.requested_at, r.summary, r.urgency, r.due_at, r.requested_by, r.consoleApplied ? "yes" : "no", r.woocommerce_done_at, r.merchant_done_at, r.note])]
                    .map((l) => l.map(csvCell).join(",")).join("\r\n"))}>Export requests ({rows.length})</button>
        </div>
      </div>
      <div className="sheet-scroll">
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Requested</th><th>Request</th><th>Due</th><th>1 Console</th><th>2 WooCommerce</th><th>3 Merchant Center</th><th>Note</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ cursor: "default" }}>
                <td className="mono">{when(r.requested_at)}<div className="meta" style={{ margin: 0 }}>{r.requested_by}</div></td>
                <td className="wrap">{r.summary}</td>
                <td style={{ color: r.overdue ? "var(--bad)" : undefined, fontWeight: r.overdue ? 600 : undefined }}>
                  {r.isClosed ? "Done" : `${r.urgency === "immediate" ? "Immediate · " : ""}${when(r.due_at)}`}{r.overdue ? " · OVERDUE" : ""}
                </td>
                <td>{r.consoleApplied ? "✓ applied" : <span title="The derived layer does not reflect it yet; it rebuilds after a rule change">waiting{r.progress ? ` (${r.progress.excludedInLayer}/${r.progress.matches})` : ""}</span>}</td>
                <td>{r.wcDone ? <span>✓ {r.woocommerce_done_by}<div className="meta" style={{ margin: 0 }}>{when(r.woocommerce_done_at)}</div><button type="button" onClick={() => tick(r.id, "woocommerce", false)}>Undo</button></span>
                  : <button type="button" onClick={() => tick(r.id, "woocommerce", true)}>Mark done</button>}</td>
                <td>{r.gmcDone ? <span>✓ {r.merchant_done_by}<div className="meta" style={{ margin: 0 }}>{when(r.merchant_done_at)}</div><button type="button" onClick={() => tick(r.id, "merchant", false)}>Undo</button></span>
                  : <button type="button" onClick={() => tick(r.id, "merchant", true)}>Mark done</button>}</td>
                <td className="wrap">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="meta" style={{ padding: 16 }}>No {status === "all" ? "" : status + " "}requests.</p>}
      </div>
    </div>
  );
}
